import * as React from 'react'

import { Button, InputSelect, InputText, Modal, Popover } from '@lmc/ui-kit'

import { useTranslation } from 'react-i18next'
import { Form } from 'antd'
import { useEffect, useState } from 'react'
import colors from '@lmc/common/constants'
import { showToast } from '../../../../../common/utils'
import { ResidentNeed } from '@lmc/ts-common'
import { ProfileNeedOption } from '../ResidentNeedsScreen'
import { getNominative } from '../../../../../common/utils'

type NeedsModalProps = {
  isModalVisible: boolean
  handleToogleDeleteModal?: () => void
  handleToggleModal?: () => void
  profileNeedOptions?: ProfileNeedOption[]
  profileNeedDetail?: ResidentNeed
  carePlanNominative?: string
  addNeed?: (need: ResidentNeed, id: string) => Promise<unknown>
  updateNeed?: (
    need: ResidentNeed,
    id: string,
    needId: string,
  ) => Promise<unknown>
  residentId?: string
  needs?: ResidentNeed[]
  testId?: string
}

export const NeedsModal = ({
  handleToggleModal,
  handleToogleDeleteModal,
  isModalVisible,
  profileNeedOptions = [],
  profileNeedDetail,
  carePlanNominative,
  addNeed,
  updateNeed,
  residentId,
  needs,
  testId,
}: NeedsModalProps): React.ReactElement => {
  const { t } = useTranslation()

  const [form] = Form.useForm()

  const onConfirm = () => {
    form.submit()
  }

  const [loading, setLoading] = useState(false)

  const handleCloseForm = () => {
    handleToggleModal?.()
    handleCleanForm()
  }

  const handleCleanForm = () => {
    form.resetFields()
    form.setFieldsValue({})
    setLoading(false)
  }

  useEffect(() => {
    if (profileNeedDetail) {
      form.setFieldsValue(profileNeedDetail)
      handleToggleModal?.()
    }
    if (!profileNeedDetail) {
      handleCleanForm()
    }
  }, [profileNeedDetail])

  const handleFinish = () => {
    setLoading(false)
    showToast({ title: t('general.forms.successfullySaved') })
    handleCloseForm()
  }

  const onFinish = async (values: { need: string; notes: string }) => {
    setLoading(true)

    try {
      let res

      if (profileNeedDetail) {
        res = await updateNeed?.(
          { need: values.need, notes: values.notes } as unknown as ResidentNeed,
          residentId as string,
          profileNeedDetail.id,
        )
      } else {
        res = await addNeed?.(
          { need: values.need, notes: values.notes } as unknown as ResidentNeed,
          residentId as string,
        )
      }

      if (res) {
        handleFinish()
      }
      setLoading(false)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    }
  }

  const canDeleteNeed = !!profileNeedDetail?.carePlans?.length

  const renderDeleteButton = profileNeedDetail && (
    <Button
      variant="danger"
      onClick={handleToogleDeleteModal}
      disabled={canDeleteNeed}
      data-testid={
        testId
          ? `${testId}-needs-modal-delete-button`
          : 'needs-modal-delete-button'
      }
    >
      {t('skillsAndNeeds.deleteNeed')}
    </Button>
  )

  return (
    <Modal
      localisation={{
        title: t(
          `skillsAndNeeds.${
            profileNeedDetail ? 'editAssignNeed' : 'assignNewNeed'
          }`,
        ),
        confirmText: t(`general.buttons.confirm`),
        cancelText: t(`general.buttons.cancel`),
      }}
      onConfirm={onConfirm}
      onCancel={handleCloseForm}
      isModalVisible={isModalVisible}
      closeButton
      closeOnConfirm={false}
      noPadding
      confirmButtonProps={{ isLoading: loading, variant: 'primary' }}
      data-testid={testId ? `${testId}-needs-modal` : 'needs-modal'}
      extraFooterElement={
        canDeleteNeed ? (
          <Popover
            content={
              <div style={styles.toolTip}>
                {t('skillsAndNeeds.cannotDeleteNeed', {
                  variable: getNominative(carePlanNominative, 'Care Plan'),
                })}
              </div>
            }
          >
            <div>{renderDeleteButton}</div>
          </Popover>
        ) : (
          renderDeleteButton
        )
      }
    >
      <Form
        form={form}
        onFinish={onFinish}
        data-testid={testId ? `${testId}-needs-modal-form` : 'needs-modal-form'}
      >
        <InputSelect
          errorMessage={t('skillsAndNeeds.form.needRequired')}
          type="need"
          label={t('skillsAndNeeds.form.selectNeed')}
          layout="column"
          placeholder={t('skillsAndNeeds.form.selectNeed')}
          groupedOptions={profileNeedOptions?.map((needOption) => ({
            label: needOption.name,
            options: needOption.needs?.map((need) => ({
              label: need.name,
              value: need.id,
              disabled: needs?.map((d) => d.need).includes(need.id) || false,
            })),
          }))}
          required
          disabled={!!profileNeedDetail}
          data-testid={
            testId ? `${testId}-needs-modal-select` : 'needs-modal-select'
          }
        />
        <div style={styles.divider}>
          <InputText
            type="notes"
            label={t('skillsAndNeeds.form.notes')}
            layout="column"
            style={{ width: '100%' }}
            data-testid={
              testId ? `${testId}-needs-modal-notes` : 'needs-modal-notes'
            }
          />
        </div>
      </Form>
    </Modal>
  )
}

const styles = {
  toolTip: {
    maxWidth: 323,
  },
  toolTipDescription: {
    fontWeight: 800,
  },
  divider: {
    borderBottom: '1px solid',
    borderColor: colors.grey,
    marginBottom: 5,
    paddingBottom: 10,
  },
  questionIcon: { marginBottom: '-3px', marginLeft: 5, marginTop: 7 },
}

export default NeedsModal
